import { Navigate, Outlet } from "react-router";

import { useMemo } from "react";

import { useSession } from "@/api/queries/auth";
import { useProductCounts, useRequestCounts } from "@/api/queries/platformCatalog";
import { AppShell } from "@/components/shell/AppShell";
import type { NavMode } from "@/components/shell/nav";
import { UserMenu } from "@/components/shell/UserMenu";
import { WorkspaceSwitcher } from "@/components/shell/WorkspaceSwitcher";
import { useUserPreference } from "@/hooks/usePreferences";

import { platformSections } from "./navigation";

/** Kasten platform area. The API independently requires platform permissions. */
export function PlatformLayout() {
  const { data: session } = useSession();
  // No tenant membership exists here, so the platform navigation mode is a global preference.
  const [mode, setMode] = useUserPreference("platform_nav_mode");
  const capabilities = session?.platform.capabilities;
  const catalog = Boolean(capabilities?.includes("core.platform_manage_catalog")) && !session?.impersonation;
  const counts = useRequestCounts(catalog).data;
  const pending = counts ? counts.manufacturer + counts.supplier + counts.branch : 0;
  const pendingProducts = useProductCounts(catalog).data?.pending ?? 0;
  const sections = useMemo(
    () =>
      platformSections(capabilities ?? []).map((s) => ({
        ...s,
        items: s.items.map((i) =>
          i.to === "/platform/catalog/requests" ? { ...i, badge: pending } : i.to === "/platform/catalog/products" ? { ...i, badge: pendingProducts } : i,
        ),
      })),
    [capabilities, pending, pendingProducts],
  );
  if (!session?.user) return null;
  if (session.impersonation) return <Navigate to={`/t/${session.impersonation.tenant_slug}`} replace />;
  if (session.platform.capabilities.length === 0) return <Navigate to="/" replace />;
  return (
    <AppShell
      sections={sections}
      mode={mode as NavMode}
      onModeChange={setMode}
      workspaceLabel="Kasten platform"
      workspace={(compact) => (
        <WorkspaceSwitcher
          compact={compact}
          name="Kasten platform"
          memberships={session.memberships}
          platform
          impersonating={false}
          subtitle="Platform administration"
        />
      )}
      railFooter={<UserMenu user={session.user} profilePath="/platform/profile" />}
    >
      <Outlet />
    </AppShell>
  );
}
